import { defineMessages } from 'react-intl';

/**
 * Copy for the MCP site picker.
 *
 * Some ids read oddly against their copy: `.label.sites` and
 * `.placeholder.plural` were suffixed back when this screen ran alongside a
 * second MCP implementation, and `.noWorkspaces.learnMore` names a state the
 * sentence no longer mentions. Each keeps the id it was first extracted under
 * so existing extractions and translations keep resolving — renaming one drops
 * its copy silently.
 */
export const mcpSiteSelectorMessages = defineMessages({
  siteLabel: {
    id: 'oauth.mcp.siteSelector.label.sites',
    defaultMessage: 'Site (For Jira, Confluence and other tools)',
    description: 'Label above the site-picker dropdown',
  },
  sitePlaceholder: {
    id: 'oauth.mcp.siteSelector.placeholder.plural',
    defaultMessage: 'Select',
    description: 'Placeholder text inside the multi-select site picker',
  },
  noSites: {
    id: 'oauth.mcp.siteSelector.noSites',
    defaultMessage:
      "Your account isn't associated with a supported site. Rovo MCP connects with Atlassian sites that use apps like Jira or Confluence. {learnMoreLink}",
    description:
      'Message below the disabled site picker when the user has no supported sites; {learnMoreLink} links to Rovo MCP documentation',
  },
  noPermissions: {
    id: 'oauth.mcp.siteSelector.noPermissions',
    defaultMessage:
      'Permissions are blocked for this site, contact your admin or {learnMoreLink}',
    description:
      'Message below the site picker when the permission check succeeded but returned no permissions; {learnMoreLink} links to the admin guide for managing MCP permissions',
  },
  noPermissionsLearnMore: {
    id: 'oauth.mcp.siteSelector.noPermissions.learnMore',
    defaultMessage: 'see how permissions are managed',
    description:
      'Link text, inside a sentence, pointing at the admin guide for managing MCP permissions',
  },
  orgConstraint: {
    id: 'oauth.mcp.siteSelector.orgConstraint',
    defaultMessage:
      'Only sites from the same organization as your selections can be selected',
    description:
      'Helper below the site picker when a site or Bitbucket workspace is already selected, so other-org sites are listed but disabled',
  },
  noOrgSites: {
    id: 'oauth.mcp.siteSelector.noOrgSites',
    defaultMessage:
      'No sites from the same organization as your selections are available',
    description:
      'Helper below the site picker when every listed site is in a different organization from the current selections',
  },
  singleSiteOrg: {
    id: 'oauth.mcp.siteSelector.singleSiteOrg',
    defaultMessage:
      'Your selection allows only one site at a time. Remove the selected site to choose another',
    description:
      'Helper below the site picker when the selection belongs to an organization that does not allow selecting several sites',
  },
  sandboxLozenge: {
    id: 'oauth.mcp.siteSelector.sandboxLozenge',
    defaultMessage: 'Sandbox',
    description:
      'Lozenge beside a site name in the site picker, marking the site as a sandbox',
  },
  sandboxConstraint: {
    id: 'oauth.mcp.siteSelector.sandboxConstraint',
    defaultMessage:
      'Sandbox sites can only be selected with other sandbox sites, and not with production sites or Bitbucket workspaces',
    description:
      'Helper below the site picker explaining why some sites are disabled: sandbox and production sites cannot be granted together',
  },
  multiSelectHint: {
    id: 'oauth.mcp.siteSelector.multiSelectHint',
    defaultMessage:
      'Choose up to {maxSelected} sites from the same organization',
    description:
      'Default helper below the site picker when several sites can be selected; {maxSelected} is the selection cap',
  },
  noWorkspacesLearnMore: {
    id: 'oauth.mcp.siteSelector.noWorkspaces.learnMore',
    defaultMessage: 'Understand Rovo MCP Server',
    description:
      'Link text for the Rovo MCP documentation shown when no workspaces are available',
  },
});
